import { zodResolver } from "@hookform/resolvers/zod";
import {
  Grid,
  TextField,
  Checkbox,
  FormControlLabel,
  InputLabel,
} from "@mui/material";
import { IDropdownModel } from "common/models/IDropdownModel";
import { AutoCompleteElement } from "components/AutoComplete";
import { FormContainer } from "components/Containers/FormContainer";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import request from "../api/memberTypewiseSectionConfig.requests";
import {
  IMemberTypewiseSectionConfig,
  MemberTypewiseSectionConfigFormProps,
  MemberTypewiseSectionConfigSchema,
} from "../api/memberTypewiseSectionConfig.types";
import TimePickerElement from "components/TimePickerElement";
import { GetSectionValues } from "features/section/api/hooks";
import { GetMemberTypeValues } from "features/memberType/api/hooks";

const Form = (props: MemberTypewiseSectionConfigFormProps) => {
  const editMode = props.memberTypewiseSectionConfig !== undefined;

  const {
    register,
    handleSubmit,
    control,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<IMemberTypewiseSectionConfig>({
    defaultValues: props.memberTypewiseSectionConfig,
    resolver: zodResolver(MemberTypewiseSectionConfigSchema),
  });

  const { data: memberTypeQuery } = GetMemberTypeValues();
  const [memberTypeOptions, setMemberTypeOptions] = useState<IDropdownModel[]>(
    []
  );

  const { data: sectionQuery } = GetSectionValues();
  const [sectionOptions, setSectionOptions] = useState<IDropdownModel[]>([]);

  useEffect(() => {
    setMemberTypeOptions(memberTypeQuery?.apiData || []);
    setSectionOptions(sectionQuery?.apiData || []);
  }, [memberTypeQuery, sectionQuery]);

  return (
    <>
      <FormContainer
        dataKey="memberTypewiseSectionConfig"
        id={props.memberTypewiseSectionConfig?.memberTypewiseSectionConfigID}
        isSubmitting={isSubmitting}
        handleSubmit={handleSubmit}
        setError={setError}
        breakPoint={4}
        editMode={editMode}
        mutation={{
          mutationFn: editMode ? request.update : request.create,
        }}
      >
        <Grid
          container
          spacing={2}
          sx={{ mb: 2 }}
          alignItems="center"
          justifyContent={"flex-start"}
        >
          <Grid item xs={4}>
            <InputLabel
              sx={{
                textAlign: "end",
                fontWeight: "bold",
              }}
              htmlFor="Member Type"
            >
              Member Type <span style={{ color: "red" }}>*</span>
            </InputLabel>
          </Grid>
          <Grid item xs={8} md={6}>
            <AutoCompleteElement
              options={memberTypeOptions}
              control={control}
              name="memberTypeID"
              placeholder="Select Member Type"
            />
          </Grid>
        </Grid>

        <Grid
          container
          spacing={2}
          sx={{ mb: 2 }}
          alignItems="center"
          justifyContent={"flex-start"}
        >
          <Grid item xs={4}>
            <InputLabel
              sx={{
                textAlign: "end",
                fontWeight: "bold",
              }}
              htmlFor="Section"
            >
              Section <span style={{ color: "red" }}>*</span>
            </InputLabel>
          </Grid>
          <Grid item xs={8} md={6}>
            <AutoCompleteElement
              options={sectionOptions}
              control={control}
              name="sectionID"
              placeholder="Select Section"
            />
          </Grid>
        </Grid>

        <Grid
          container
          columnSpacing={3}
          sx={{ mb: 1 }}
          alignItems="center"
          justifyContent={"flex-start"}
        >
          <Grid item xs={4}></Grid>
          <Grid item xs={8} md={6}>
            <FormControlLabel
              control={
                <Checkbox
                  defaultChecked={
                    props.memberTypewiseSectionConfig?.isOvernightIssue ?? true
                  }
                />
              }
              label="Is Overnight Issue?"
              {...register("isOvernightIssue")}
            />
          </Grid>
        </Grid>

        <Grid
          container
          columnSpacing={3}
          sx={{ mb: 2 }}
          alignItems="center"
          justifyContent={"flex-start"}
        >
          <Grid item xs={4}></Grid>
          <Grid item xs={8} md={6}>
            <FormControlLabel
              control={
                <Checkbox
                  defaultChecked={
                    props.memberTypewiseSectionConfig?.isOnPremisesIssue ?? true
                  }
                />
              }
              label="Is On Premises Issue?"
              {...register("isOnPremisesIssue")}
            />
          </Grid>
        </Grid>

        <Grid
          container
          spacing={2}
          sx={{ mb: 2 }}
          alignItems="center"
          justifyContent={"flex-start"}
        >
          <Grid item xs={4}>
            <InputLabel
              sx={{
                textAlign: "end",
                fontWeight: "bold",
              }}
              htmlFor="Over night Issue Max Items"
            >
              Over night Issue Max Items <span style={{ color: "red" }}>*</span>
            </InputLabel>
          </Grid>
          <Grid item xs={8} md={6}>
            <TextField
              {...register("overnightIssueMaxItems")}
              fullWidth
              placeholder="Enter Over night Issue Max Items"
              error={!!errors.overnightIssueMaxItems}
              helperText={
                errors.overnightIssueMaxItems
                  ? errors.overnightIssueMaxItems?.message
                  : ""
              }
            />
          </Grid>
        </Grid>

        <Grid
          container
          spacing={2}
          sx={{ mb: 2 }}
          alignItems="center"
          justifyContent={"flex-start"}
        >
          <Grid item xs={4}>
            <InputLabel
              sx={{
                textAlign: "end",
                fontWeight: "bold",
              }}
              htmlFor="On Premises Issue End Time"
            >
              On Premises Issue End Time
            </InputLabel>
          </Grid>

          <Grid item xs={8} md={6}>
            <TimePickerElement
              control={control}
              name="onPremisesIssueEndTime"
              label="hh:mm:ss aa"
              format="hh:mm:ss A"
            ></TimePickerElement>
          </Grid>
        </Grid>

        <Grid
          container
          spacing={2}
          sx={{ mb: 2 }}
          alignItems="center"
          justifyContent={"flex-start"}
        >
          <Grid item xs={4}>
            <InputLabel
              sx={{
                textAlign: "end",
                fontWeight: "bold",
              }}
              htmlFor="Remarks"
            >
              Remarks
            </InputLabel>
          </Grid>
          <Grid item xs={8} md={6}>
            <TextField
              {...register("remarks")}
              multiline={true}
              rows={3}
              fullWidth
              placeholder="Enter Remarks"
              error={!!errors.remarks}
              helperText={errors.remarks ? errors.remarks?.message : ""}
            />
          </Grid>
        </Grid>
      </FormContainer>
    </>
  );
};

export default Form;
